import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { AdminShell } from '../components/layout/AdminShell';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { FieldHelp, FieldHelpGroup, SectionHelp } from '../components/FieldHelp';
import { Modal } from '../components/Modal';
import { StatusPill } from '../components/StatusPill';
import { useToast } from '../components/ToastHost';
import { manufacturedHelp } from '../lib/fieldHelpCopy';
import {
  createBatch,
  createManufacturedBatch,
  getManufactured,
  getManufacturedProvisioning,
  getManufacturedTelemetryHealth,
  listBatches,
  listCatalog,
  listClients,
  listManufactured,
  resetManufacturedClient,
  resetManufacturedIot,
  syncManufacturedIot,
  updateBatch,
  updateManufactured,
} from '../lib/adminApi';
import { previewBatchId } from '../lib/batchId';
import type {
  ManufacturedRecord,
  ManufacturedStatus,
  ManufacturedTelemetryHealth,
  ManufacturingBatch,
  ManufacturingBatchStatus,
} from '../types/registry';
import { can, useStaffMe } from '../lib/permissions';

const HW_STATUSES: ManufacturedStatus[] = ['AVAILABLE', 'DESTROYED', 'FAULTY', 'DEV_USE', 'SOLD', 'BOUND'];
const BATCH_STATUSES: ManufacturingBatchStatus[] = ['OPEN', 'CLOSED'];
const FULL_MAC_RE = /^[0-9A-F]{12}$/;

function formatEpochMs(value?: number): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Date(value).toLocaleString();
}

function dash(value?: string | null): string {
  return value ? value : '—';
}

/** Strip separators/punctuation; return uppercase hex only. */
function normalizeMacQuery(raw: string): string {
  return raw.replace(/[^0-9A-Fa-f]/g, '').toUpperCase();
}

/** True when the query (ignoring spaces/:/ -) is only hex — MAC search, not a name. */
function isMacLikeQuery(raw: string): boolean {
  const compact = raw.replace(/[\s:\-]/g, '');
  return compact.length > 0 && /^[0-9A-Fa-f]+$/i.test(compact);
}

function ClientName({
  clientId,
  nameById,
}: {
  clientId?: string | null;
  nameById: Map<string, string>;
}): JSX.Element {
  const id = (clientId || '').trim();
  if (!id) return <>{'—'}</>;
  const label = nameById.get(id) || id;
  return <span title={id}>{label}</span>;
}

const MAX_REGISTER_MACS = 100;

/** Comma-only list → unique uppercase 12-hex MACs, or a validation error. */
function parseManufacturedMacList(raw: string): { macs: string[] } | { error: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { error: 'Enter at least one MAC' };
  const tokens = trimmed.split(',');
  const macs: string[] = [];
  const seen = new Set<string>();
  for (const token of tokens) {
    const piece = token.trim();
    if (!piece) {
      return { error: 'Empty MAC entry — use commas only between MACs (no trailing or double commas)' };
    }
    if (/\s/.test(piece)) {
      return { error: 'MACs must be comma-separated (no spaces inside a MAC token)' };
    }
    const mac = piece.replace(/[:\-]/g, '').toUpperCase();
    if (!FULL_MAC_RE.test(mac)) {
      return { error: `Invalid MAC (need 12 hex digits): ${piece}` };
    }
    if (seen.has(mac)) continue;
    seen.add(mac);
    macs.push(mac);
  }
  if (macs.length === 0) return { error: 'Enter at least one MAC' };
  if (macs.length > MAX_REGISTER_MACS) {
    return { error: `At most ${MAX_REGISTER_MACS} MACs per registration (got ${macs.length})` };
  }
  return { macs };
}

function IconRefresh(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 12a8 8 0 1 1-2.3-5.6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M20 4v5h-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ProvisioningDetailModal({
  mac,
  onClose,
  canSyncIot,
  canReset,
  clientNameById,
}: {
  mac: string;
  onClose: () => void;
  canSyncIot: boolean;
  canReset: boolean;
  clientNameById: Map<string, string>;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { success } = useToast();
  const [confirmResetIot, setConfirmResetIot] = useState(false);
  const [confirmResetClient, setConfirmResetClient] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['manufactured-provisioning', mac],
    queryFn: () => getManufacturedProvisioning(mac),
  });
  const syncMutation = useMutation({
    mutationFn: () => syncManufacturedIot(mac),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['manufactured-provisioning', mac] });
      await queryClient.invalidateQueries({ queryKey: ['manufactured'] });
    },
  });
  const resetIotMutation = useMutation({
    mutationFn: () => resetManufacturedIot(mac),
    onSuccess: async () => {
      setConfirmResetIot(false);
      success(`IoT reset complete for ${mac}`);
      await queryClient.invalidateQueries({ queryKey: ['manufactured-provisioning', mac] });
      await queryClient.invalidateQueries({ queryKey: ['manufactured'] });
    },
  });
  const resetClientMutation = useMutation({
    mutationFn: () => resetManufacturedClient(mac),
    onSuccess: async () => {
      setConfirmResetClient(false);
      success(`Client reset complete for ${mac}`);
      await queryClient.invalidateQueries({ queryKey: ['manufactured-provisioning', mac] });
      await queryClient.invalidateQueries({ queryKey: ['manufactured'] });
    },
  });

  const data = detailQuery.data;
  const mfg = data?.manufacturing;
  const registry = data?.registry;
  const iot = data?.iot;

  let iotExistsLabel = '—';
  if (iot?.exists === true) iotExistsLabel = 'yes';
  if (iot?.exists === false) iotExistsLabel = 'no';
  if (iot?.exists === null) iotExistsLabel = iot.error ? `unknown (${iot.error})` : 'unknown';

  return (
    <Modal title={`Provisioning — ${mac}`} onClose={onClose}>
      <div className="login-form">
        {detailQuery.isLoading ? <p>Loading live status…</p> : null}
        {detailQuery.isError ? (
          <div className="form-error">
            {detailQuery.error instanceof Error
              ? detailQuery.error.message
              : 'Failed to load provisioning detail'}
          </div>
        ) : null}
        {syncMutation.isError ? (
          <div className="form-error">
            {syncMutation.error instanceof Error
              ? syncMutation.error.message
              : 'Failed to sync IoT into manufacturing'}
          </div>
        ) : null}
        {resetIotMutation.isError ? (
          <div className="form-error">
            {resetIotMutation.error instanceof Error
              ? resetIotMutation.error.message
              : 'Failed to reset IoT'}
          </div>
        ) : null}
        {resetClientMutation.isError ? (
          <div className="form-error">
            {resetClientMutation.error instanceof Error
              ? resetClientMutation.error.message
              : 'Failed to reset client'}
          </div>
        ) : null}
        {data ? (
          <>
            <section className="catalog-form__section">
              <div className="catalog-form__section-head">
                <h3>Manufacturing (Portal)</h3>
                {canSyncIot ? (
                  <button
                    type="button"
                    className="table-icon-button"
                    aria-label="Sync manufacturing from IoT Core"
                    title="Sync manufacturing from IoT Core"
                    disabled={syncMutation.isPending}
                    onClick={() => syncMutation.mutate()}
                  >
                    <IconRefresh />
                  </button>
                ) : null}
              </div>
              <p>
                <span className="hint-text">Status</span>
                <br />
                <StatusPill status={mfg?.provisioning_status} />
              </p>
              <p>
                <span className="hint-text">Thing name (stamped)</span>
                <br />
                {mfg?.iot_thing_name ? <code>{mfg.iot_thing_name}</code> : '—'}
              </p>
              <p>
                <span className="hint-text">Provisioned at</span>
                <br />
                {formatEpochMs(mfg?.iot_provisioned_at)}
              </p>
              <p>
                <span className="hint-text">Current client</span>
                <br />
                <ClientName clientId={mfg?.current_client_id} nameById={clientNameById} />
              </p>
              <p>
                <span className="hint-text">Last client</span>
                <br />
                <ClientName clientId={mfg?.last_client_id} nameById={clientNameById} />
              </p>
              <p>
                <span className="hint-text">Last bound at</span>
                <br />
                {formatEpochMs(mfg?.last_client_bound_at)}
              </p>
            </section>
            <section className="catalog-form__section">
              <h3>Device registry (Core)</h3>
              {data.registry_error ? (
                <div className="form-error">{data.registry_error}</div>
              ) : null}
              {registry ? (
                <>
                  <p>
                    <span className="hint-text">Device ID</span>
                    <br />
                    <code>{dash(registry.device_id)}</code>
                  </p>
                  <p>
                    <span className="hint-text">Client</span>
                    <br />
                    <ClientName clientId={registry.client_id} nameById={clientNameById} />
                  </p>
                  <p>
                    <span className="hint-text">Registry status</span>
                    <br />
                    <StatusPill status={registry.status} />
                  </p>
                  <p>
                    <span className="hint-text">Provisioning status</span>
                    <br />
                    <StatusPill status={registry.provisioning_status} />
                  </p>
                  <p>
                    <span className="hint-text">Thing name</span>
                    <br />
                    {registry.iot_thing_name ? <code>{registry.iot_thing_name}</code> : '—'}
                  </p>
                  <p>
                    <span className="hint-text">Provisioned at</span>
                    <br />
                    {formatEpochMs(registry.provisioned_at)}
                  </p>
                </>
              ) : (
                <p className="hint-text">No device-registry row for this MAC.</p>
              )}
            </section>
            <section className="catalog-form__section">
              <h3>IoT Core (live)</h3>
              <p>
                <span className="hint-text">Thing name</span>
                <br />
                <code>{iot?.thing_name || `sense-${mac}`}</code>
              </p>
              <p>
                <span className="hint-text">Thing exists</span>
                <br />
                {iotExistsLabel}
              </p>
            </section>
          </>
        ) : null}
        <div className="modal-card__actions">
          {canReset ? (
            <>
              <button
                className="ghost-button"
                type="button"
                onClick={() => setConfirmResetClient(true)}
                disabled={resetClientMutation.isPending}
              >
                Reset client
              </button>
              <button
                className="ghost-button"
                type="button"
                onClick={() => setConfirmResetIot(true)}
                disabled={resetIotMutation.isPending}
              >
                Reset IoT
              </button>
            </>
          ) : (
            <p className="hint-text" role="status">
              No permission granted for Reset client / Reset IoT.
            </p>
          )}
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmResetIot}
        title="Reset IoT provisioning"
        message={`This will delete the IoT thing sense-${mac}, its certificates, and policies. The device must re-Fleet Provision (claim cert → RegisterThing) to reconnect. Continue?`}
        confirmLabel="Reset IoT"
        danger
        busy={resetIotMutation.isPending}
        onCancel={() => setConfirmResetIot(false)}
        onConfirm={() => resetIotMutation.mutate()}
      />

      <ConfirmDialog
        open={confirmResetClient}
        title="Reset client binding"
        message={`This will unbind the device from its current client, revoke active tokens in Core, and set provisioning to PENDING. If the device is BOUND it will become SOLD. Continue?`}
        confirmLabel="Reset client"
        danger
        busy={resetClientMutation.isPending}
        onCancel={() => setConfirmResetClient(false)}
        onConfirm={() => resetClientMutation.mutate()}
      />
    </Modal>
  );
}

function TelemetryHealthModal({
  mac,
  onClose,
  clientNameById,
}: {
  mac: string;
  onClose: () => void;
  clientNameById: Map<string, string>;
}): JSX.Element {
  const healthQuery = useQuery({
    queryKey: ['manufactured-telemetry-health', mac],
    queryFn: () => getManufacturedTelemetryHealth(mac),
  });
  const data: ManufacturedTelemetryHealth | undefined = healthQuery.data;
  const dh = data?.data_health;

  return (
    <Modal
      title={`Telemetry health — ${mac}`}
      onClose={onClose}
      wide
      headerActions={
        <button
          type="button"
          className="table-icon-button"
          aria-label="Refresh telemetry health"
          title="Refresh telemetry health"
          disabled={healthQuery.isFetching}
          onClick={() => void healthQuery.refetch()}
        >
          <IconRefresh />
        </button>
      }
    >
      <div className="login-form">
        {healthQuery.isLoading ? <p>Loading live health…</p> : null}
        {healthQuery.isError ? (
          <div className="form-error">
            {healthQuery.error instanceof Error
              ? healthQuery.error.message
              : 'Failed to load telemetry health'}
          </div>
        ) : null}
        {data ? (
          <>
            {!data.registry_found ? (
              <p className="hint-text">
                No Core device-registry row for this MAC — device is not client-bound /
                installed.
              </p>
            ) : null}
            {data.registry_error ? <div className="form-error">{data.registry_error}</div> : null}
            {data.events_error ? <div className="form-error">{data.events_error}</div> : null}

            <section className="catalog-form__section">
              <h3>Operational</h3>
              <p>
                <span className="hint-text">State</span>
                <br />
                <StatusPill status={data.state} />
              </p>
              <p>
                <span className="hint-text">Provisioning</span>
                <br />
                <StatusPill status={data.provisioning_status} />
              </p>
              <p>
                <span className="hint-text">Device ID</span>
                <br />
                {data.device_id ? <code>{data.device_id}</code> : '—'}
              </p>
              <p>
                <span className="hint-text">Client</span>
                <br />
                <ClientName clientId={data.client_id} nameById={clientNameById} />
              </p>
            </section>

            <section className="catalog-form__section">
              <h3>Connection</h3>
              <p>
                <span className="hint-text">Status</span>
                <br />
                <StatusPill status={data.connection?.status} />
              </p>
              <p>
                <span className="hint-text">Age (seconds)</span>
                <br />
                {data.connection?.age_seconds != null ? data.connection.age_seconds : '—'}
              </p>
              <p>
                <span className="hint-text">Avg interval (seconds)</span>
                <br />
                {data.connection?.avg_interval_seconds != null
                  ? data.connection.avg_interval_seconds
                  : '—'}
              </p>
              <p>
                <span className="hint-text">Samples used</span>
                <br />
                {data.connection?.samples_used ?? '—'}
              </p>
            </section>

            <section className="catalog-form__section">
              <h3>Last seen</h3>
              <p>
                <span className="hint-text">Last telemetry</span>
                <br />
                {dash(data.last_seen)}
              </p>
              <p>
                <span className="hint-text">Hours since last message</span>
                <br />
                {data.hours_since_last_message != null ? data.hours_since_last_message : '—'}
              </p>
              <p>
                <span className="hint-text">Messages (24h)</span>
                <br />
                {data.messages_last_24h ?? '—'}
              </p>
              <p>
                <span className="hint-text">Battery %</span>
                <br />
                {data.bat_level_pct != null ? data.bat_level_pct : '—'}
              </p>
              <p>
                <span className="hint-text">First seen (epoch ms)</span>
                <br />
                {data.first_seen != null ? formatEpochMs(data.first_seen) : '—'}
              </p>
            </section>

            <section className="catalog-form__section">
              <h3>Thresholds</h3>
              <p>
                <span className="hint-text">Offline after (seconds)</span>
                <br />
                {data.offline_after_seconds ?? '—'}
              </p>
              <p>
                <span className="hint-text">Expected interval (seconds)</span>
                <br />
                {data.expected_interval_seconds ?? '—'}
              </p>
            </section>

            <section className="catalog-form__section">
              <h3>Data health</h3>
              {dh ? (
                <>
                  <p>
                    <span className="hint-text">Status</span>
                    <br />
                    <StatusPill status={typeof dh.status === 'string' ? dh.status : undefined} />
                  </p>
                  <p>
                    <span className="hint-text">Score</span>
                    <br />
                    {dh.score != null ? String(dh.score) : '—'}
                  </p>
                  <p>
                    <span className="hint-text">Messages count</span>
                    <br />
                    {dh.messages_count != null ? String(dh.messages_count) : '—'}
                  </p>
                  <p>
                    <span className="hint-text">Last message at</span>
                    <br />
                    {dash(typeof dh.last_message_at === 'string' ? dh.last_message_at : undefined)}
                  </p>
                </>
              ) : (
                <p className="hint-text">No analytics rollup available for this device.</p>
              )}
            </section>
          </>
        ) : null}
        <div className="modal-card__actions">
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function ManufacturedDevicesPage(): JSX.Element {
  const meQuery = useStaffMe();
  const me = meQuery.data;
  const canCreateItem = can(me, 'manufactured_devices', 'create');
  const canEditItem = can(me, 'manufactured_devices', 'edit');
  const canTelemetryHealth = can(me, 'manufactured_devices', 'telemetry_health');
  const canResetDevice = can(me, 'manufactured_devices', 'reset');
  const canCreateBatch = can(me, 'manufacturing_batches', 'create');
  const canEditBatch = can(me, 'manufacturing_batches', 'edit');
  const queryClient = useQueryClient();
  const { success } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'batches' ? 'batches' : 'items';
  const macSearch = searchParams.get('q') ?? '';

  const [batchFilter, setBatchFilter] = useState('');
  const [hwStatusFilter, setHwStatusFilter] = useState('');
  const [typeIdFilter, setTypeIdFilter] = useState('');
  const [activeBatch, setActiveBatch] = useState<string | undefined>(undefined);
  const [activeHwStatus, setActiveHwStatus] = useState<ManufacturedStatus | undefined>(undefined);
  const [activeTypeId, setActiveTypeId] = useState<string | undefined>(undefined);
  const [filterOpen, setFilterOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<ManufacturedRecord | null>(null);
  const [provDetailRow, setProvDetailRow] = useState<ManufacturedRecord | null>(null);
  const [healthRow, setHealthRow] = useState<ManufacturedRecord | null>(null);
  const [editStatus, setEditStatus] = useState<ManufacturedStatus>('AVAILABLE');
  const [editNotes, setEditNotes] = useState('');
  const [confirmStatusChange, setConfirmStatusChange] = useState(false);

  const [mac, setMac] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [macTypeIds, setMacTypeIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmRegister, setConfirmRegister] = useState(false);
  const [pendingMacs, setPendingMacs] = useState<string[]>([]);

  const [createBatchOpen, setCreateBatchOpen] = useState(false);
  const [editBatchRow, setEditBatchRow] = useState<ManufacturingBatch | null>(null);
  const [batchDate, setBatchDate] = useState('');
  const [batchSize, setBatchSize] = useState('1');
  const [batchTypeIds, setBatchTypeIds] = useState<string[]>([]);
  const [batchNotes, setBatchNotes] = useState('');
  const [editBatchDate, setEditBatchDate] = useState('');
  const [editBatchSize, setEditBatchSize] = useState('1');
  const [editBatchStatus, setEditBatchStatus] = useState<ManufacturingBatchStatus>('OPEN');
  const [editBatchNotes, setEditBatchNotes] = useState('');
  const [editBatchTypeIds, setEditBatchTypeIds] = useState<string[]>([]);

  const listQuery = useQuery({
    queryKey: ['manufactured', activeBatch ?? ''],
    queryFn: () => listManufactured(activeBatch),
  });
  const catalogQuery = useQuery({ queryKey: ['catalog'], queryFn: listCatalog });
  const batchesQuery = useQuery({ queryKey: ['batches'], queryFn: listBatches });
  const clientsQuery = useQuery({ queryKey: ['clients'], queryFn: listClients });

  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const client of clientsQuery.data ?? []) {
      if (client.client_id) map.set(client.client_id, client.display_name || client.client_id);
    }
    return map;
  }, [clientsQuery.data]);

  const listRows = listQuery.data ?? [];
  const searchRaw = macSearch.trim();
  const normalizedMacSearch = normalizeMacQuery(macSearch);
  const macLikeSearch = isMacLikeQuery(searchRaw);
  const filteredRows = useMemo(() => {
    return listRows.filter((row) => {
      if (activeHwStatus && row.status !== activeHwStatus) return false;
      if (activeTypeId && !(row.type_ids || []).includes(activeTypeId)) return false;
      if (!searchRaw) return true;
      const qLower = searchRaw.toLowerCase();
      const macMatch =
        macLikeSearch && normalizedMacSearch.length > 0 && row.mac.includes(normalizedMacSearch);
      const clientId = (row.current_client_id || '').toLowerCase();
      const clientName = (clientNameById.get(row.current_client_id || '') || '').toLowerCase();
      const clientMatch =
        (clientName && clientName.includes(qLower)) ||
        (clientId && clientId.includes(qLower));
      return Boolean(macMatch || clientMatch);
    });
  }, [
    listRows,
    searchRaw,
    macLikeSearch,
    normalizedMacSearch,
    clientNameById,
    activeHwStatus,
    activeTypeId,
  ]);

  const filterSummary = useMemo(() => {
    const parts: string[] = [];
    if (activeBatch) parts.push(`batch ${activeBatch}`);
    if (activeHwStatus) parts.push(`HW ${activeHwStatus}`);
    if (activeTypeId) {
      const name = catalogQuery.data?.items?.find((i) => i.type_id === activeTypeId)?.type_name;
      parts.push(`type ${name || activeTypeId}`);
    }
    return parts;
  }, [activeBatch, activeHwStatus, activeTypeId, catalogQuery.data]);

  const lookupMac =
    macLikeSearch &&
    FULL_MAC_RE.test(normalizedMacSearch) &&
    !listRows.some((row) => row.mac === normalizedMacSearch)
      ? normalizedMacSearch
      : null;

  const macLookupQuery = useQuery({
    queryKey: ['manufactured', 'by-mac', lookupMac],
    queryFn: () => getManufactured(lookupMac!),
    enabled: Boolean(lookupMac),
    retry: false,
  });

  const displayRows = useMemo(() => {
    const lookupItem = macLookupQuery.data;
    if (!lookupItem) return filteredRows;
    if (filteredRows.some((row) => row.mac === lookupItem.mac)) return filteredRows;
    return [lookupItem, ...filteredRows];
  }, [filteredRows, macLookupQuery.data]);

  const catalogById = useMemo(
    () => new Map((catalogQuery.data?.items ?? []).map((item) => [item.type_id, item])),
    [catalogQuery.data],
  );
  const openBatches = useMemo(
    () => (batchesQuery.data ?? []).filter((b) => b.status === 'OPEN'),
    [batchesQuery.data],
  );
  const selectedBatch = useMemo(
    () => (batchesQuery.data ?? []).find((b) => b.batch_id === selectedBatchId) ?? null,
    [batchesQuery.data, selectedBatchId],
  );
  const batchIdPreview = useMemo(
    () =>
      previewBatchId(
        batchDate,
        batchTypeIds,
        catalogQuery.data?.items ?? [],
        (batchesQuery.data ?? []).map((b) => b.batch_id),
      ),
    [batchDate, batchTypeIds, catalogQuery.data, batchesQuery.data],
  );

  const createMutation = useMutation({
    mutationFn: (macs: string[]) =>
      createManufacturedBatch({
        macs,
        batch_id: selectedBatchId,
        type_ids: macTypeIds,
        status: 'AVAILABLE',
        provisioning_status: 'UNKNOWN',
        notes: notes || undefined,
      }),
    onSuccess: async (result) => {
      setConfirmRegister(false);
      setPendingMacs([]);
      await queryClient.invalidateQueries({ queryKey: ['manufactured'] });
      if (result.failed_count === 0) {
        setMac('');
        setNotes('');
        setMacTypeIds([]);
        setCreateOpen(false);
        setError(null);
        success('All MACs registered');
        return;
      }
      const list = result.failed_macs.join(', ');
      setError(
        `${result.failed_count} MAC(s) could not be registered: ${list}`,
      );
      success(
        `Registered ${result.created_count}; ${result.failed_count} failed`,
      );
    },
    onError: (err: Error) => {
      setConfirmRegister(false);
      setError(err.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      mac: m,
      status,
      notes: n,
    }: {
      mac: string;
      status: ManufacturedStatus;
      notes: string;
    }) => updateManufactured(m, { status, notes: n }),
    onSuccess: async (updated) => {
      setEditRow(null);
      setConfirmStatusChange(false);
      setError(null);
      success(`Device ${updated.mac} updated`);
      await queryClient.invalidateQueries({ queryKey: ['manufactured'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const createBatchMutation = useMutation({
    mutationFn: () =>
      createBatch({
        batch_date: batchDate.trim(),
        batch_size: Number(batchSize) || 1,
        type_ids: batchTypeIds,
        notes: batchNotes || undefined,
        status: 'OPEN',
      }),
    onSuccess: async (created) => {
      setCreateBatchOpen(false);
      setBatchDate('');
      setBatchSize('1');
      setBatchTypeIds([]);
      setBatchNotes('');
      setError(null);
      success(`Batch ${created.batch_id} created`);
      await queryClient.invalidateQueries({ queryKey: ['batches'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateBatchMutation = useMutation({
    mutationFn: () => {
      if (!editBatchRow) throw new Error('No batch selected');
      return updateBatch(editBatchRow.batch_id, {
        batch_date: editBatchDate.trim(),
        batch_size: Number(editBatchSize) || 1,
        status: editBatchStatus,
        notes: editBatchNotes,
        type_ids: editBatchTypeIds,
      });
    },
    onSuccess: async (updated) => {
      setEditBatchRow(null);
      setError(null);
      success(`Batch ${updated.batch_id} updated`);
      await queryClient.invalidateQueries({ queryKey: ['batches'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  function setTab(next: 'items' | 'batches') {
    const params = new URLSearchParams(searchParams);
    if (next === 'items') params.delete('tab');
    else params.set('tab', 'batches');
    setSearchParams(params);
    setError(null);
  }

  function updateMacSearch(value: string) {
    const next = new URLSearchParams(searchParams);
    const trimmed = value.trim();
    if (trimmed) next.set('q', trimmed);
    else next.delete('q');
    setSearchParams(next);
  }

  function handleCreate(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const parsed = parseManufacturedMacList(mac);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    setPendingMacs(parsed.macs);
    setConfirmRegister(true);
  }

  function confirmRegisterMacs() {
    if (pendingMacs.length === 0) return;
    setError(null);
    createMutation.mutate(pendingMacs);
  }

  function openEdit(row: ManufacturedRecord) {
    setError(null);
    setConfirmStatusChange(false);
    setEditRow(row);
    setEditStatus(
      HW_STATUSES.includes(row.status as ManufacturedStatus)
        ? (row.status as ManufacturedStatus)
        : 'AVAILABLE',
    );
    setEditNotes(row.notes ?? '');
  }

  function handleEdit(event: FormEvent) {
    event.preventDefault();
    if (!editRow) return;
    setError(null);
    if (editStatus === 'DESTROYED' || editStatus === 'FAULTY') {
      setConfirmStatusChange(true);
      return;
    }
    updateMutation.mutate({ mac: editRow.mac, status: editStatus, notes: editNotes });
  }

  function confirmDestructiveStatusChange() {
    if (!editRow) return;
    setError(null);
    updateMutation.mutate({ mac: editRow.mac, status: editStatus, notes: editNotes });
  }

  function toggleType(list: string[], id: string, setter: (next: string[]) => void) {
    setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  }

  function openEditBatch(row: ManufacturingBatch) {
    setError(null);
    setEditBatchRow(row);
    setEditBatchDate(row.batch_date);
    setEditBatchSize(String(row.batch_size));
    setEditBatchStatus(row.status === 'CLOSED' ? 'CLOSED' : 'OPEN');
    setEditBatchNotes(row.notes ?? '');
    setEditBatchTypeIds(row.type_ids ? [...row.type_ids] : []);
  }

  function onSelectBatchForMac(batchId: string) {
    setSelectedBatchId(batchId);
    const batch = (batchesQuery.data ?? []).find((b) => b.batch_id === batchId);
    const ids = batch?.type_ids ?? [];
    setMacTypeIds(ids.length === 1 ? [ids[0]] : []);
  }

  return (
    <AdminShell title="Manufactured devices" subtitle="Batch & MAC allowlist">
      <div className="admin-tabs" role="tablist" aria-label="Manufactured sections">
        <button
          type="button"
          role="tab"
          id="tab-items"
          aria-selected={tab === 'items'}
          aria-controls="panel-items"
          className={`admin-tab${tab === 'items' ? ' admin-tab--active' : ''}`}
          onClick={() => setTab('items')}
        >
          Items
        </button>
        <button
          type="button"
          role="tab"
          id="tab-batches"
          aria-selected={tab === 'batches'}
          aria-controls="panel-batches"
          className={`admin-tab${tab === 'batches' ? ' admin-tab--active' : ''}`}
          onClick={() => setTab('batches')}
        >
          Batches
        </button>
      </div>

      {tab === 'items' ? (
        <div className="panel" id="panel-items" role="tabpanel" aria-labelledby="tab-items">
          <div className="panel-toolbar">
            {filterSummary.length > 0 ? (
              <div>
                <h2>Manufactured devices</h2>
                <p className="panel-toolbar__meta">Filtered to {filterSummary.join(' · ')}</p>
              </div>
            ) : (
              <p className="panel-toolbar__meta">All manufactured devices</p>
            )}
            <div className="panel-toolbar__actions">
              <button
                className="secondary-button"
                type="button"
                onClick={() => {
                  setError(null);
                  setBatchFilter(activeBatch ?? '');
                  setHwStatusFilter(activeHwStatus ?? '');
                  setTypeIdFilter(activeTypeId ?? '');
                  setFilterOpen(true);
                }}
              >
                Filter
              </button>
              {canCreateItem ? (
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => {
                    setError(null);
                    const first = openBatches[0];
                    setSelectedBatchId(first?.batch_id ?? '');
                    const ids = first?.type_ids ?? [];
                    setMacTypeIds(ids.length === 1 ? [ids[0]] : []);
                    setCreateOpen(true);
                  }}
                >
                  Register MAC
                </button>
              ) : null}
            </div>
          </div>
          {listQuery.isLoading ? <p>Loading…</p> : null}
          {listQuery.isError ? (
            <div className="form-error">
              {listQuery.error instanceof Error
                ? listQuery.error.message
                : 'Failed to load manufactured devices'}
            </div>
          ) : null}
          {error && !createOpen && !editRow && !filterOpen ? (
            <div className="form-error">{error}</div>
          ) : null}
          <div className="table-filters">
            <label>
              <span>Search</span>
              <input
                value={macSearch}
                onChange={(e) => updateMacSearch(e.target.value)}
                placeholder="Search by MAC or client"
                autoComplete="off"
                spellCheck={false}
              />
            </label>
          </div>
          {macLookupQuery.isFetching ? <p className="hint-text">Looking up MAC…</p> : null}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>MAC</th>
                  <th>Batch</th>
                  <th>Client</th>
                  <th>HW status</th>
                  <th>Provisioning</th>
                  <th>Device type</th>
                  <th>Notes</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {!macSearch.trim() &&
                listRows.length === 0 &&
                !listQuery.isLoading &&
                !listQuery.isError ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="empty-state">
                        <p className="empty-state__title">No manufactured devices yet</p>
                        <p className="empty-state__copy">
                          {activeBatch
                            ? 'No devices registered against this batch yet.'
                            : 'Register the first MAC once an open batch exists.'}
                        </p>
                        {canCreateItem && openBatches.length > 0 ? (
                          <button
                            className="primary-button"
                            type="button"
                            onClick={() => {
                              setError(null);
                              const first = openBatches[0];
                              setSelectedBatchId(first?.batch_id ?? '');
                              const ids = first?.type_ids ?? [];
                              setMacTypeIds(ids.length === 1 ? [ids[0]] : []);
                              setCreateOpen(true);
                            }}
                          >
                            Register MAC
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ) : null}
                {macSearch.trim() &&
                displayRows.length === 0 &&
                !listQuery.isLoading &&
                !macLookupQuery.isFetching &&
                !listQuery.isError ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="empty-state">
                        <p className="empty-state__title">No devices match this search</p>
                        <p className="empty-state__copy">
                          Try a different MAC or client name, clear search, or check MAC separators
                          (colons/dashes are ignored).
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : null}
                {displayRows.map((row) => (
                  <tr key={row.mac}>
                    <td>
                      <code>{row.mac}</code>
                    </td>
                    <td>
                      {row.batch_id}
                      <br />
                      <span className="hint-text">{row.batch_date}</span>
                    </td>
                    <td>
                      <ClientName clientId={row.current_client_id} nameById={clientNameById} />
                    </td>
                    <td>
                      <StatusPill status={row.status} />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="table-link"
                        onClick={() => setProvDetailRow(row)}
                      >
                        <StatusPill status={row.provisioning_status} />
                      </button>
                    </td>
                    <td>
                      <div className="table-link-list">
                        {(row.type_ids || []).map((typeId) => {
                          const deviceType = catalogById.get(typeId);
                          return (
                            <Link
                              key={typeId}
                              className="table-link"
                              to={`/device-catalog?q=${encodeURIComponent(typeId)}`}
                              title={typeId}
                            >
                              {deviceType?.type_name || typeId}
                            </Link>
                          );
                        })}
                      </div>
                    </td>
                    <td>{row.notes || '—'}</td>
                    <td className="table-actions">
                      {canTelemetryHealth ? (
                        <button
                          type="button"
                          className="ghost-button"
                          onClick={() => setHealthRow(row)}
                        >
                          Health
                        </button>
                      ) : null}
                      {canEditItem ? (
                        <button type="button" className="ghost-button" onClick={() => openEdit(row)}>
                          Edit
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="panel" id="panel-batches" role="tabpanel" aria-labelledby="tab-batches">
          <div className="panel-toolbar">
            <p className="panel-toolbar__meta">Manufacturing batches</p>
            <div className="panel-toolbar__actions">
              {canCreateBatch ? (
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => {
                    setError(null);
                    setCreateBatchOpen(true);
                  }}
                >
                  Create batch
                </button>
              ) : null}
            </div>
          </div>
          {batchesQuery.isLoading ? <p>Loading…</p> : null}
          {batchesQuery.isError ? (
            <div className="form-error">
              {batchesQuery.error instanceof Error
                ? batchesQuery.error.message
                : 'Failed to load batches'}
            </div>
          ) : null}
          {error && !createBatchOpen && !editBatchRow ? <div className="form-error">{error}</div> : null}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Batch ID</th>
                  <th>Date</th>
                  <th>Size</th>
                  <th>Status</th>
                  <th>Types</th>
                  <th>Notes</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(batchesQuery.data ?? []).length === 0 &&
                !batchesQuery.isLoading &&
                !batchesQuery.isError ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty-state">
                        <p className="empty-state__title">No batches yet</p>
                        <p className="empty-state__copy">
                          Create a batch before registering manufactured MACs.
                        </p>
                        {canCreateBatch ? (
                          <button
                            className="primary-button"
                            type="button"
                            onClick={() => {
                              setError(null);
                              setCreateBatchOpen(true);
                            }}
                          >
                            Create batch
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ) : null}
                {(batchesQuery.data ?? []).map((row) => (
                  <tr key={row.batch_id}>
                    <td>
                      <code>{row.batch_id}</code>
                    </td>
                    <td>{row.batch_date}</td>
                    <td>{row.batch_size}</td>
                    <td>
                      <StatusPill status={row.status} />
                    </td>
                    <td>
                      <div className="table-link-list">
                        {(row.type_ids || []).map((typeId) => (
                          <span key={typeId}>
                            {catalogById.get(typeId)?.type_name || typeId}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>{row.notes || '—'}</td>
                    <td className="table-actions">
                      {canEditBatch ? (
                        <button
                          type="button"
                          className="ghost-button"
                          onClick={() => openEditBatch(row)}
                        >
                          Edit
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filterOpen ? (
        <Modal title="Filter devices" onClose={() => setFilterOpen(false)}>
          <form
            className="login-form"
            onSubmit={(e) => {
              e.preventDefault();
              setActiveBatch(batchFilter.trim() || undefined);
              const status = hwStatusFilter.trim();
              setActiveHwStatus(
                status && HW_STATUSES.includes(status as ManufacturedStatus)
                  ? (status as ManufacturedStatus)
                  : undefined,
              );
              setActiveTypeId(typeIdFilter.trim() || undefined);
              setFilterOpen(false);
            }}
          >
            <label>
              <span>Batch</span>
              <select value={batchFilter} onChange={(e) => setBatchFilter(e.target.value)}>
                <option value="">All batches</option>
                {(batchesQuery.data ?? []).map((b) => (
                  <option key={b.batch_id} value={b.batch_id}>
                    {b.batch_id}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>HW status</span>
              <select value={hwStatusFilter} onChange={(e) => setHwStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                {HW_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Device type</span>
              <select value={typeIdFilter} onChange={(e) => setTypeIdFilter(e.target.value)}>
                <option value="">All types</option>
                {(catalogQuery.data?.items ?? []).map((item) => (
                  <option key={item.type_id} value={item.type_id}>
                    {item.type_name || item.type_id}
                  </option>
                ))}
              </select>
            </label>
            <div className="modal-card__actions">
              <button className="primary-button" type="submit">
                Apply
              </button>
              <button
                className="ghost-button"
                type="button"
                onClick={() => {
                  setBatchFilter('');
                  setHwStatusFilter('');
                  setTypeIdFilter('');
                  setActiveBatch(undefined);
                  setActiveHwStatus(undefined);
                  setActiveTypeId(undefined);
                  setFilterOpen(false);
                }}
              >
                Clear
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {createOpen && canCreateItem ? (
        <Modal
          title="Register MAC"
          onClose={() => setCreateOpen(false)}
          closeOnBackdrop={false}
          wide
        >
          {openBatches.length === 0 ? (
            <p className="modal-card__copy">
              No OPEN batches. Create a batch on the Batches tab first.
            </p>
          ) : (
            <form className="login-form" onSubmit={handleCreate}>
              <FieldHelpGroup>
                <FieldHelp label="MAC (12 hex)" help={manufacturedHelp.mac}>
                  <textarea
                    value={mac}
                    onChange={(e) => setMac(e.target.value)}
                    required
                    rows={3}
                    placeholder="AABBCCDDEEFF or AABBCCDDEEFF,112233445566"
                    aria-label="MACs (12 hex each, comma-separated)"
                  />
                </FieldHelp>
                <FieldHelp label="Batch" help={manufacturedHelp.batchRegister}>
                  <select
                    value={selectedBatchId}
                    onChange={(e) => onSelectBatchForMac(e.target.value)}
                    required
                  >
                    {openBatches.map((b) => (
                      <option key={b.batch_id} value={b.batch_id}>
                        {b.batch_id} — {b.batch_date} — size {b.batch_size}
                      </option>
                    ))}
                  </select>
                </FieldHelp>
                {selectedBatch ? (
                  <p className="hint-text">
                    Date {selectedBatch.batch_date} · size {selectedBatch.batch_size} (from batch)
                  </p>
                ) : null}
                <SectionHelp title="Device type" help={manufacturedHelp.typesRegister}>
                  <fieldset className="type-multiselect">
                    {(selectedBatch?.type_ids ?? []).map((typeId) => (
                      <label key={typeId} className="catalog-form__check">
                        <input
                          type="radio"
                          name="mac-device-type"
                          checked={macTypeIds.length === 1 && macTypeIds[0] === typeId}
                          onChange={() => setMacTypeIds([typeId])}
                        />
                        <span>{catalogById.get(typeId)?.type_name || typeId}</span>
                      </label>
                    ))}
                  </fieldset>
                </SectionHelp>
                <FieldHelp label="Notes" help={manufacturedHelp.notesItem}>
                  <input value={notes} onChange={(e) => setNotes(e.target.value)} />
                </FieldHelp>
              </FieldHelpGroup>
              {error ? <div className="form-error">{error}</div> : null}
              <div className="modal-card__actions">
                <button
                  className="primary-button"
                  type="submit"
                  disabled={createMutation.isPending || macTypeIds.length !== 1}
                >
                  {createMutation.isPending ? 'Creating…' : 'Create'}
                </button>
                <button className="ghost-button" type="button" onClick={() => setCreateOpen(false)}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </Modal>
      ) : null}

      <ConfirmDialog
        open={confirmRegister}
        title="Confirm MAC registration"
        message={
          pendingMacs.length === 1
            ? `Register MAC ${pendingMacs[0]} into batch ${selectedBatchId}?`
            : `Register ${pendingMacs.length} MACs into batch ${selectedBatchId}?`
        }
        confirmLabel="Register"
        busy={createMutation.isPending}
        onCancel={() => {
          if (createMutation.isPending) return;
          setConfirmRegister(false);
          setPendingMacs([]);
        }}
        onConfirm={confirmRegisterMacs}
      />

      {editRow && canEditItem ? (
        <Modal
          title="Edit manufactured device"
          onClose={() => setEditRow(null)}
          closeOnBackdrop={false}
        >
          <form className="login-form" onSubmit={handleEdit}>
            <FieldHelpGroup>
              <label>
                <span>MAC</span>
                <input value={editRow.mac} disabled />
              </label>
              <FieldHelp label="Hardware status" help={manufacturedHelp.hardwareStatus}>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as ManufacturedStatus)}
                >
                  {HW_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s === 'DEV_USE' ? 'DEV USE' : s}
                    </option>
                  ))}

                </select>
              </FieldHelp>
              {editStatus === 'DESTROYED' || editStatus === 'FAULTY' ? (
                <p className="hint-text">
                  Saving will ask for confirmation — this hardware status usually can't be reversed.
                </p>
              ) : null}
              {editStatus === 'SOLD' || editStatus === 'BOUND' ? (
                <p className="hint-text">
                  SOLD = sold to a client (not yet bound); BOUND = actively bound to a client.
                </p>
              ) : null}
              <FieldHelp label="Notes" help={manufacturedHelp.notesItem}>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  placeholder="Manufacturing / QA notes"
                />
              </FieldHelp>
            </FieldHelpGroup>
            {error ? <div className="form-error">{error}</div> : null}
            <div className="modal-card__actions">
              <button className="primary-button" type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <button className="ghost-button" type="button" onClick={() => setEditRow(null)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      <ConfirmDialog
        open={confirmStatusChange && editRow !== null}
        title={editStatus === 'DESTROYED' ? 'Mark device destroyed' : 'Mark device faulty'}
        message={`Set ${editRow?.mac} to ${editStatus}? This hardware status usually can't be reversed.`}
        confirmLabel="Save status"
        danger
        busy={updateMutation.isPending}
        onCancel={() => setConfirmStatusChange(false)}
        onConfirm={confirmDestructiveStatusChange}
      />

      {provDetailRow ? (
        <ProvisioningDetailModal
          mac={provDetailRow.mac}
          onClose={() => setProvDetailRow(null)}
          canSyncIot={canEditItem}
          canReset={canResetDevice}
          clientNameById={clientNameById}
        />
      ) : null}

      {healthRow && canTelemetryHealth ? (
        <TelemetryHealthModal
          mac={healthRow.mac}
          onClose={() => setHealthRow(null)}
          clientNameById={clientNameById}
        />
      ) : null}

      {createBatchOpen && canCreateBatch ? (
        <Modal
          title="Create batch"
          onClose={() => setCreateBatchOpen(false)}
          closeOnBackdrop={false}
          wide
        >
          <form
            className="login-form"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              createBatchMutation.mutate();
            }}
          >
            <FieldHelpGroup>
              <SectionHelp title="Device types" help={manufacturedHelp.typesCreateBatch}>
                <fieldset className="type-multiselect">
                  {(catalogQuery.data?.items ?? []).map((row) => (
                    <label key={row.type_id} className="catalog-form__check">
                      <input
                        type="checkbox"
                        checked={batchTypeIds.includes(row.type_id)}
                        onChange={() => toggleType(batchTypeIds, row.type_id, setBatchTypeIds)}
                      />
                      <span>
                        {row.type_name} <code className="hint-text">{row.type_id}</code>
                      </span>
                    </label>
                  ))}
                </fieldset>
              </SectionHelp>
              <FieldHelp label="Batch date" help={manufacturedHelp.batchDate}>
                <input
                  type="date"
                  value={batchDate}
                  onChange={(e) => setBatchDate(e.target.value)}
                  required
                />
              </FieldHelp>
              <FieldHelp label="Batch size" help={manufacturedHelp.batchSize}>
                <input
                  type="number"
                  min={1}
                  value={batchSize}
                  onChange={(e) => setBatchSize(e.target.value)}
                  required
                />
              </FieldHelp>
              <FieldHelp label="Notes" help={manufacturedHelp.notesBatch}>
                <input value={batchNotes} onChange={(e) => setBatchNotes(e.target.value)} />
              </FieldHelp>
            </FieldHelpGroup>
            <p className="hint-text">
              Batch ID preview:{' '}
              <code>{batchIdPreview || 'select types and date'}</code> (final ID from server)
            </p>
            {error ? <div className="form-error">{error}</div> : null}
            <div className="modal-card__actions">
              <button
                className="primary-button"
                type="submit"
                disabled={createBatchMutation.isPending || batchTypeIds.length === 0}
              >
                {createBatchMutation.isPending ? 'Creating…' : 'Create'}
              </button>
              <button
                className="ghost-button"
                type="button"
                onClick={() => setCreateBatchOpen(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {editBatchRow && canEditBatch ? (
        <Modal
          title="Edit batch"
          onClose={() => setEditBatchRow(null)}
          closeOnBackdrop={false}
          wide
        >
          <form
            className="login-form"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              updateBatchMutation.mutate();
            }}
          >
            <FieldHelpGroup>
              <label>
                <span>Batch ID</span>
                <input value={editBatchRow.batch_id} disabled />
              </label>
              <SectionHelp title="Device types" help={manufacturedHelp.typesEditBatch}>
                <fieldset className="type-multiselect">
                  {(catalogQuery.data?.items ?? []).map((row) => (
                    <label key={row.type_id} className="catalog-form__check">
                      <input
                        type="checkbox"
                        checked={editBatchTypeIds.includes(row.type_id)}
                        onChange={() =>
                          toggleType(editBatchTypeIds, row.type_id, setEditBatchTypeIds)
                        }
                      />
                      <span>
                        {row.type_name} <code className="hint-text">{row.type_id}</code>
                      </span>
                    </label>
                  ))}
                </fieldset>
              </SectionHelp>
              <FieldHelp label="Batch date" help={manufacturedHelp.batchDate}>
                <input
                  type="date"
                  value={editBatchDate}
                  onChange={(e) => setEditBatchDate(e.target.value)}
                  required
                />
              </FieldHelp>
              <FieldHelp label="Batch size" help={manufacturedHelp.batchSize}>
                <input
                  type="number"
                  min={1}
                  value={editBatchSize}
                  onChange={(e) => setEditBatchSize(e.target.value)}
                  required
                />
              </FieldHelp>
              <FieldHelp label="Status" help={manufacturedHelp.batchStatus}>
                <select
                  value={editBatchStatus}
                  onChange={(e) => setEditBatchStatus(e.target.value as ManufacturingBatchStatus)}
                >
                  {BATCH_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </FieldHelp>
              <FieldHelp label="Notes" help={manufacturedHelp.notesBatch}>
                <textarea
                  value={editBatchNotes}
                  onChange={(e) => setEditBatchNotes(e.target.value)}
                  rows={3}
                />
              </FieldHelp>
            </FieldHelpGroup>
            {error ? <div className="form-error">{error}</div> : null}
            <div className="modal-card__actions">
              <button
                className="primary-button"
                type="submit"
                disabled={updateBatchMutation.isPending || editBatchTypeIds.length === 0}
              >
                {updateBatchMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <button className="ghost-button" type="button" onClick={() => setEditBatchRow(null)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
    </AdminShell>
  );
}
